interface PageScrollLockState {
  scrollTop: number;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
  bodyOverflow: string;
  htmlOverflow: string;
}

let lockCount = 0;
let lockState: PageScrollLockState | null = null;

export function usePageScrollLock(): {
  lockPageScroll: () => void;
  unlockPageScroll: () => void;
} {
  let isLockedByInstance = false;
  let restoreFocusEl: HTMLElement | null = null;

  const lockPageScroll = (): void => {
    if (import.meta.server || isLockedByInstance) {
      return;
    }

    const { documentElement, body, activeElement } = document;
    restoreFocusEl =
      activeElement instanceof HTMLElement && !activeElement.closest('[role="dialog"]')
        ? activeElement
        : null;

    if (lockCount === 0) {
      const scrollTop =
        window.scrollY || documentElement.scrollTop || body.scrollTop || 0;

      lockState = {
        scrollTop,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        bodyOverflow: body.style.overflow,
        htmlOverflow: documentElement.style.overflow,
      };

      body.style.position = "fixed";
      body.style.top = `-${scrollTop}px`;
      body.style.width = "100%";
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
    }

    lockCount += 1;
    isLockedByInstance = true;
  };

  const unlockPageScroll = (): void => {
    if (import.meta.server || !isLockedByInstance) {
      return;
    }

    isLockedByInstance = false;
    lockCount = Math.max(0, lockCount - 1);

    if (lockCount > 0) {
      restoreFocusEl = null;
      return;
    }

    const state = lockState;
    lockState = null;

    if (!state) {
      restoreFocusEl = null;
      return;
    }

    const { documentElement, body } = document;
    const focusTarget = restoreFocusEl;

    body.style.position = state.bodyPosition;
    body.style.top = state.bodyTop;
    body.style.width = state.bodyWidth;
    body.style.overflow = state.bodyOverflow;
    documentElement.style.overflow = state.htmlOverflow;

    window.scrollTo({ top: state.scrollTop, left: 0, behavior: "instant" });
    restoreFocusEl = null;

    if (focusTarget !== null && documentElement.contains(focusTarget)) {
      focusTarget.focus({ preventScroll: true });
    }
  };

  return { lockPageScroll, unlockPageScroll };
}
